import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { firstValueFrom } from 'rxjs';
import { I18nService } from '../../../../core/i18n';
import { VERTICAL_OPTIONS, type Vertical } from '../../../../core/enums/verticals';
import { CompanyContextService } from '../../../../core/tenant/company-context.service';
import { CompanyStore } from '../../../company-context/store/company.store';
import { CompanyVerticalApiService } from '../../../company-context/services/company-vertical-api.service';
import { SidebarStore } from '../../../../layout/sidebar/store/sidebar.store';
import { ProjectsFacade } from '../../../projects/facades/projects.facade';
import { TPipe } from '../../../../shared/pipes';
import { ToastService } from '../../../../shared/ui/toast/toast.service';
import { projectIconKeyToApi } from '../../../../shared/utils/project-icon-key.util';
import {
  WORKFLOW_ACCENT_COLOR_PALETTE,
  WORKFLOW_FORM_ICON_KEYS,
} from '../../../../shared/utils/workflow-form-appearance.util';
import type {
  InstallSystemWorkflowTemplateRequest,
  SystemWorkflowTemplate,
} from '../../models/system-workflow-template.model';
import { SystemWorkflowTemplatesApiService } from '../../services/system-workflow-templates-api.service';
import { OnboardingChecklistStore } from '../../stores/onboarding-checklist.store';
import { OnboardingStore } from '../../stores/onboarding.store';

const MAX_TEMPLATES = 3;

interface PartialFailureState {
  failedTemplate: SystemWorkflowTemplate;
  remaining: SystemWorkflowTemplate[];
  capturedProjectId: string | null;
  errorMessage: string;
  okCount: number;
  totalCount: number;
}

@Component({
  selector: 'app-onboarding-step-template',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, TPipe],
  template: `
    <div class="flex flex-col gap-5 px-1 py-2">
      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-[var(--muted)]">
          {{ 'onboarding.progress' | t: { current: 2, total: 4 } }}
        </p>
        <h2 class="mt-1 text-xl font-semibold text-[var(--text)]">
          {{ 'onboarding.template.title' | t }}
        </h2>
        <p class="mt-1 text-sm text-[var(--muted)]">
          {{ 'onboarding.template.subtitle' | t }}
        </p>
      </div>

      @if (verticalLoading()) {
        <div
          class="flex items-center gap-2 rounded-xl border border-dashed border-[var(--border)] bg-[var(--accent-soft)] p-4"
        >
          <lucide-icon
            name="loader-circle"
            [size]="16"
            class="animate-spin text-[var(--muted)]"
            aria-hidden="true"
          ></lucide-icon>
          <p class="text-sm text-[var(--muted)]">
            {{ 'onboarding.template.loading' | t }}
          </p>
        </div>
      } @else if (!vertical()) {
        <div
          class="flex flex-col gap-3 rounded-xl border border-dashed border-[var(--border)] bg-[var(--accent-soft)] p-4"
        >
          <p class="text-sm text-[var(--text)]">
            {{ 'onboarding.template.verticalchoosehint' | t }}
          </p>
          <select
            class="rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text)] focus:border-[var(--accent)] focus:outline-none disabled:opacity-60"
            [ngModel]="verticalChooserValue()"
            (ngModelChange)="verticalChooserValue.set($event)"
            [disabled]="verticalChooserSaving()"
          >
            <option [ngValue]="null" disabled>
              {{ 'onboarding.template.verticalplaceholder' | t }}
            </option>
            @for (opt of verticalOptions; track opt.value) {
              <option [ngValue]="opt.value">{{ opt.label | t }}</option>
            }
          </select>
          @if (verticalChooserError()) {
            <p class="text-xs text-[var(--danger)]">{{ verticalChooserError() }}</p>
          }
          <div class="mt-1 flex flex-wrap items-center gap-3">
            <button
              type="button"
              class="rounded-xl bg-[var(--accent)] px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              [disabled]="!verticalChooserValue() || verticalChooserSaving()"
              (click)="confirmVerticalChoice()"
            >
              @if (verticalChooserSaving()) {
                {{ 'onboarding.template.verticalsaving' | t }}
              } @else {
                {{ 'onboarding.template.verticalconfirm' | t }}
              }
            </button>
            <button
              type="button"
              class="text-sm text-[var(--muted)] underline-offset-2 hover:text-[var(--text)] hover:underline disabled:cursor-not-allowed disabled:opacity-60"
              [disabled]="verticalChooserSaving()"
              (click)="skipStep()"
            >
              {{ 'onboarding.template.skip' | t }}
            </button>
          </div>
        </div>
      } @else {
        <!-- 1. Vertical detectada do cadastro da empresa -->
        <div
          class="flex items-center justify-between gap-3 rounded-xl border border-[var(--border)] bg-[var(--bg-surface)] p-4"
        >
          <div class="flex items-center gap-3">
            <span
              class="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--accent-soft)] text-[var(--accent)]"
            >
              <lucide-icon name="briefcase" [size]="18" aria-hidden="true"></lucide-icon>
            </span>
            <div class="flex flex-col">
              <span class="text-xs font-medium uppercase tracking-wide text-[var(--muted)]">
                {{ 'onboarding.template.verticallabel' | t }}
              </span>
              <span class="text-sm font-semibold text-[var(--text)]">
                {{ verticalDisplayName() }}
              </span>
            </div>
          </div>
        </div>

        <!-- 2. Projeto onde os processos serão criados -->
        <div class="flex flex-col gap-2">
          <h3 class="text-sm font-semibold text-[var(--text)]">
            {{ 'onboarding.template.projectheading' | t }}
          </h3>

          @if (!hasProjects()) {
            <p class="text-xs text-[var(--muted)]">
              {{ 'onboarding.template.projectempty' | t }}
            </p>
          } @else {
            <div class="inline-flex self-start rounded-lg border border-[var(--border)] p-1">
              <button
                type="button"
                class="rounded-md px-3 py-1.5 text-xs font-medium transition"
                [class.bg-[var(--accent)]]="effectiveProjectMode() === 'existing'"
                [class.text-white]="effectiveProjectMode() === 'existing'"
                [class.text-[var(--muted)]]="effectiveProjectMode() !== 'existing'"
                [disabled]="installing()"
                (click)="setProjectMode('existing')"
              >
                {{ 'onboarding.template.modeexisting' | t }}
              </button>
              <button
                type="button"
                class="rounded-md px-3 py-1.5 text-xs font-medium transition"
                [class.bg-[var(--accent)]]="effectiveProjectMode() === 'new'"
                [class.text-white]="effectiveProjectMode() === 'new'"
                [class.text-[var(--muted)]]="effectiveProjectMode() !== 'new'"
                [disabled]="installing()"
                (click)="setProjectMode('new')"
              >
                {{ 'onboarding.template.modenew' | t }}
              </button>
            </div>
          }

          @if (effectiveProjectMode() === 'existing') {
            <select
              class="rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text)] focus:border-[var(--accent)] focus:outline-none"
              [ngModel]="selectedProjectId()"
              (ngModelChange)="selectedProjectId.set($event)"
              [disabled]="installing()"
            >
              <option [ngValue]="null" disabled>
                {{ 'onboarding.template.projectplaceholder' | t }}
              </option>
              @for (project of projects(); track project.id) {
                <option [ngValue]="project.id">{{ project.name }}</option>
              }
            </select>
          } @else {
            <div class="flex items-start gap-2">
              <div class="relative">
                <button
                  type="button"
                  class="inline-flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg border border-[var(--border)] transition hover:border-[var(--accent)]"
                  [style.background-color]="uploadedImageDataUrl() ? null : softenColor(newProjectColor())"
                  [style.color]="newProjectColor() || 'var(--accent)'"
                  [disabled]="installing()"
                  (click)="toggleAppearancePopover()"
                  [attr.aria-label]="'onboarding.template.newprojectappearancecta' | t"
                >
                  @if (uploadedImageDataUrl(); as imgUrl) {
                    <img [src]="imgUrl" alt="" class="h-full w-full object-cover" />
                  } @else {
                    <lucide-icon
                      [name]="newProjectIcon() || 'folder'"
                      [size]="18"
                      aria-hidden="true"
                    ></lucide-icon>
                  }
                </button>
                @if (appearancePopoverOpen()) {
                  <div
                    class="absolute left-0 top-full z-20 mt-2 flex w-80 flex-col gap-3 rounded-xl border border-[var(--border)] bg-[var(--bg-surface)] p-3 shadow-lg"
                    (click)="$event.stopPropagation()"
                  >
                    <!-- Header: título + Upload + Fechar -->
                    <div class="flex items-center justify-between gap-2">
                      <strong class="text-xs font-semibold text-[var(--text)]">
                        {{ 'onboarding.template.newprojecticonlabel' | t }}
                      </strong>
                      <div class="flex items-center gap-2">
                        <button
                          type="button"
                          class="rounded-md bg-[var(--accent)] px-2.5 py-1 text-[11px] font-semibold text-white transition hover:opacity-90"
                          (click)="imageInput.click()"
                        >
                          {{ 'onboarding.template.newprojectupload' | t }}
                        </button>
                        <button
                          type="button"
                          class="text-[11px] text-[var(--muted)] hover:text-[var(--text)]"
                          (click)="closeAppearancePopover()"
                        >
                          {{ 'common.close' | t }}
                        </button>
                      </div>
                    </div>

                    <!-- Linha de cores -->
                    <div class="flex flex-wrap gap-1">
                      <button
                        type="button"
                        class="relative h-6 w-6 overflow-hidden rounded-md border-2 bg-[var(--bg-surface)]"
                        [class.border-[var(--accent)]]="!newProjectColor()"
                        [class.border-[var(--border)]]="newProjectColor()"
                        (click)="pickColor(null)"
                        [attr.aria-label]="'onboarding.template.newprojectcolornone' | t"
                        [attr.title]="'onboarding.template.newprojectcolornone' | t"
                      >
                        <span
                          class="absolute left-1/2 top-1/2 h-[140%] w-px -translate-x-1/2 -translate-y-1/2 rotate-45 bg-[var(--danger)]"
                          aria-hidden="true"
                        ></span>
                      </button>
                      @for (hex of colorPalette; track hex) {
                        <button
                          type="button"
                          class="h-6 w-6 rounded-md border-2 transition"
                          [class.border-[var(--accent)]]="newProjectColor() === hex"
                          [class.border-transparent]="newProjectColor() !== hex"
                          [style.background-color]="hex"
                          (click)="pickColor(hex)"
                          [attr.aria-label]="hex"
                        ></button>
                      }
                    </div>

                    <!-- Input file (oculto) -->
                    <input
                      #imageInput
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      class="hidden"
                      (change)="onProjectImageSelected($event)"
                    />

                    <!-- Nome do arquivo + remover -->
                    @if (uploadedImageName()) {
                      <div
                        class="flex items-center justify-between gap-2 rounded-md border border-[var(--border)] bg-[var(--accent-soft)] px-2 py-1 text-[11px]"
                      >
                        <span class="truncate text-[var(--text)]">{{ uploadedImageName() }}</span>
                        <button
                          type="button"
                          class="shrink-0 text-[var(--muted)] hover:text-[var(--danger)]"
                          (click)="removeUploadedImage()"
                        >
                          {{ 'onboarding.template.newprojectremoveimage' | t }}
                        </button>
                      </div>
                    }

                    <!-- Grid de ícones -->
                    <div class="grid max-h-44 grid-cols-7 gap-1 overflow-y-auto pr-1">
                      @for (icon of iconChoices; track icon) {
                        <button
                          type="button"
                          class="inline-flex h-8 w-8 items-center justify-center rounded-md border transition hover:border-[var(--accent)]"
                          [class.border-[var(--accent)]]="newProjectIcon() === icon"
                          [class.bg-[var(--accent-soft)]]="newProjectIcon() === icon"
                          [class.border-[var(--border)]]="newProjectIcon() !== icon"
                          (click)="pickIcon(icon)"
                        >
                          <lucide-icon [name]="icon" [size]="14"></lucide-icon>
                        </button>
                      }
                    </div>
                  </div>
                }
              </div>
              <input
                type="text"
                class="flex-1 rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text)] focus:border-[var(--accent)] focus:outline-none"
                [ngModel]="newProjectName()"
                (ngModelChange)="newProjectName.set($event)"
                [placeholder]="'onboarding.template.newprojectnameplaceholder' | t"
                [disabled]="installing()"
              />
            </div>
          }
        </div>

        <!-- 3. Templates da vertical -->
        @if (loadingTemplates()) {
          <p class="text-sm text-[var(--muted)]">{{ 'onboarding.template.loading' | t }}</p>
        } @else if (templates().length === 0) {
          <p class="text-sm text-[var(--muted)]">{{ 'onboarding.template.empty' | t }}</p>
        } @else {
          <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between text-xs text-[var(--muted)]">
              <span>{{ 'onboarding.template.selectionhint' | t }}</span>
              <span>{{
                'onboarding.template.selectioncounter'
                  | t: { selected: selectedCount(), max: maxTemplates }
              }}</span>
            </div>
            <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
              @for (tpl of templates(); track tpl.id) {
                <button
                  type="button"
                  class="relative flex flex-col items-start gap-2 rounded-xl border p-4 text-left transition disabled:cursor-not-allowed"
                  [class.border-2]="isSelected(tpl)"
                  [class.border-[var(--accent)]]="isSelected(tpl)"
                  [class.bg-[var(--accent-soft)]]="isSelected(tpl)"
                  [class.border-[var(--border)]]="!isSelected(tpl)"
                  [class.opacity-50]="!isSelected(tpl) && !canSelectMore()"
                  [class.hover:border-[var(--accent)]]="canSelectMore() || isSelected(tpl)"
                  [disabled]="(!isSelected(tpl) && !canSelectMore()) || installing()"
                  (click)="toggleTemplate(tpl)"
                >
                  @if (isSelected(tpl)) {
                    <span
                      class="absolute right-3 top-3 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[var(--accent)] text-[11px] font-bold text-white"
                      [attr.aria-label]="selectionOrder(tpl)"
                    >
                      {{ selectionOrder(tpl) }}
                    </span>
                  }
                  <span
                    class="inline-flex h-9 w-9 items-center justify-center rounded-lg"
                    [style.background-color]="softenColor(tpl.colorHex)"
                    [style.color]="tpl.colorHex || 'var(--accent)'"
                  >
                    <lucide-icon
                      [name]="tpl.iconName || 'sparkles'"
                      [size]="18"
                      aria-hidden="true"
                    ></lucide-icon>
                  </span>
                  <span class="text-sm font-semibold text-[var(--text)]">{{
                    tpl.displayName
                  }}</span>
                  <span class="text-xs text-[var(--muted)] line-clamp-2">{{
                    tpl.description
                  }}</span>
                  <span class="mt-1 text-[11px] text-[var(--muted)]">
                    {{
                      'onboarding.template.metrics'
                        | t
                          : {
                              stages: tpl.stageCount,
                              fields: tpl.fieldCount,
                              automations: tpl.automationCount
                            }
                    }}
                  </span>
                </button>
              }
            </div>
          </div>

          @if (selectedCount() > 0) {
            <div class="flex flex-col gap-2">
              <div>
                <h3 class="text-sm font-semibold text-[var(--text)]">
                  {{ 'onboarding.template.workflowsheading' | t }}
                </h3>
                <p class="text-xs text-[var(--muted)]">
                  {{ 'onboarding.template.workflowsheadinghint' | t }}
                </p>
              </div>
              <div class="flex flex-col gap-2">
                @for (tpl of selectedTemplatesOrdered(); track tpl.id) {
                  <div
                    class="flex items-center gap-3 rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] p-2"
                  >
                    <span
                      class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                      [style.background-color]="softenColor(tpl.colorHex)"
                      [style.color]="tpl.colorHex || 'var(--accent)'"
                      [attr.title]="'onboarding.template.workflowsheadinghint' | t"
                    >
                      <lucide-icon
                        [name]="tpl.iconName || 'sparkles'"
                        [size]="16"
                        aria-hidden="true"
                      ></lucide-icon>
                    </span>
                    <input
                      type="text"
                      class="flex-1 rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text)] focus:border-[var(--accent)] focus:outline-none"
                      [ngModel]="getWorkflowName(tpl.id)"
                      (ngModelChange)="setWorkflowName(tpl.id, $event)"
                      [placeholder]="'onboarding.template.workflowname' | t"
                      [disabled]="installing()"
                    />
                  </div>
                }
              </div>
            </div>
          }

          @if (errorMessage()) {
            <p class="text-sm text-[var(--danger)]">{{ errorMessage() }}</p>
          }

          @if (partialFailure(); as pf) {
            <div
              class="flex flex-col gap-2 rounded-lg border border-warning-300 bg-warning-50 p-3"
            >
              <p class="text-sm font-semibold text-warning-800">
                {{ 'onboarding.template.partialfailuretitle' | t }}
              </p>
              <p class="text-xs text-warning-800">
                {{
                  'onboarding.template.partialfailuremsg'
                    | t
                      : {
                          ok: pf.okCount,
                          total: pf.totalCount,
                          name: pf.failedTemplate.displayName,
                          error: pf.errorMessage
                        }
                }}
              </p>
              <div class="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  class="text-xs font-medium text-[var(--accent)] hover:underline"
                  [disabled]="installing()"
                  (click)="retryRemaining()"
                >
                  {{
                    'onboarding.template.partialfailureretrycta'
                      | t: { remaining: pf.remaining.length + 1 }
                  }}
                </button>
                <button
                  type="button"
                  class="text-xs font-medium text-[var(--muted)] hover:text-[var(--text)] hover:underline"
                  [disabled]="installing()"
                  (click)="continueAnyway()"
                >
                  {{ 'onboarding.template.partialfailurecontinuecta' | t }}
                </button>
              </div>
            </div>
          }
        }

        <div class="mt-2 flex flex-wrap items-center justify-between gap-3">
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="rounded-xl bg-[var(--accent)] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              [disabled]="!canInstall()"
              (click)="installAll(false)"
            >
              @if (installing()) {
                {{
                  'onboarding.template.ctaprogress'
                    | t: { index: installProgress().index, total: installProgress().total }
                }}
              } @else if (selectedCount() === 1) {
                {{ 'onboarding.template.ctamultione' | t }}
              } @else {
                {{ 'onboarding.template.ctamulti' | t: { count: selectedCount() } }}
              }
            </button>
            <button
              type="button"
              class="text-sm text-[var(--muted)] underline-offset-2 hover:text-[var(--text)] hover:underline"
              [disabled]="installing()"
              (click)="skipStep()"
            >
              {{ 'onboarding.template.skip' | t }}
            </button>
          </div>
          <button
            type="button"
            class="text-xs text-[var(--muted)] underline-offset-2 hover:text-[var(--text)] hover:underline"
            [disabled]="installing()"
            (click)="snoozeNow()"
          >
            {{ 'onboarding.snooze' | t }}
          </button>
        </div>
      }
    </div>
  `,
})
export class OnboardingStepTemplateComponent {
  private readonly api = inject(SystemWorkflowTemplatesApiService);
  private readonly store = inject(OnboardingStore);
  private readonly checklistStore = inject(OnboardingChecklistStore);
  private readonly projectsFacade = inject(ProjectsFacade);
  private readonly sidebarStore = inject(SidebarStore);
  private readonly companyContext = inject(CompanyContextService);
  private readonly companyStore = inject(CompanyStore);
  private readonly verticalApi = inject(CompanyVerticalApiService);
  private readonly http = inject(HttpClient);
  private readonly toast = inject(ToastService);
  private readonly i18n = inject(I18nService);

  protected readonly maxTemplates = MAX_TEMPLATES;
  protected readonly iconChoices = WORKFLOW_FORM_ICON_KEYS;
  protected readonly colorPalette = WORKFLOW_ACCENT_COLOR_PALETTE;
  protected readonly verticalOptions = VERTICAL_OPTIONS.map((opt) => ({
    value: opt.value,
    label: opt.labelKey,
  }));

  protected readonly templates = signal<SystemWorkflowTemplate[]>([]);
  protected readonly loadingTemplates = signal(false);
  protected readonly selectedTemplateIds = signal<string[]>([]);
  protected readonly workflowNameByTemplateId = signal<Record<string, string>>({});
  protected readonly projectMode = signal<'existing' | 'new'>('existing');
  protected readonly selectedProjectId = signal<string | null>(null);
  protected readonly newProjectName = signal('');
  protected readonly newProjectColor = signal<string | null>(null);
  protected readonly newProjectIcon = signal<string | null>(null);
  protected readonly appearancePopoverOpen = signal(false);
  protected readonly installing = signal(false);
  protected readonly installProgress = signal<{ index: number; total: number }>({
    index: 0,
    total: 0,
  });
  protected readonly partialFailure = signal<PartialFailureState | null>(null);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly verticalChooserValue = signal<Vertical | null>(null);
  protected readonly verticalChooserSaving = signal(false);
  protected readonly verticalChooserError = signal<string | null>(null);
  protected readonly uploadedProjectImageFile = signal<File | null>(null);
  protected readonly uploadedImageDataUrl = signal<string | null>(null);
  protected readonly uploadedImageName = signal('');

  /**
   * Distingue 3 estados pra evitar o flash do estado "vertical missing"
   * enquanto a hidratação async ainda não chegou:
   * - `undefined` → não-conhecido (loading): mostra spinner.
   * - `null`      → backend confirmou que não há vertical: mostra fallback.
   * - `Vertical`  → carrega templates daquela vertical.
   */
  private readonly verticalRaw = computed(() => {
    const company = this.companyContext.activeCompany();
    if (!company) return undefined;
    return company.vertical;
  });
  protected readonly verticalLoading = computed(() => this.verticalRaw() === undefined);
  protected readonly vertical = computed(() => this.verticalRaw() ?? null);
  protected readonly verticalDisplayName = computed(() => {
    const v = this.vertical();
    if (!v) return '';
    const opt = VERTICAL_OPTIONS.find((o) => o.value === v);
    return opt ? this.i18n.t(opt.labelKey) : '';
  });
  protected readonly projects = computed(() => this.projectsFacade.projects());
  protected readonly hasProjects = computed(() => this.projects().length > 0);
  protected readonly effectiveProjectMode = computed<'existing' | 'new'>(() =>
    this.hasProjects() ? this.projectMode() : 'new'
  );
  protected readonly selectedTemplatesOrdered = computed(() => {
    const map = new Map(this.templates().map((tpl) => [tpl.id, tpl] as const));
    return this.selectedTemplateIds()
      .map((id) => map.get(id))
      .filter((tpl): tpl is SystemWorkflowTemplate => !!tpl);
  });
  protected readonly selectedCount = computed(() => this.selectedTemplateIds().length);
  protected readonly canSelectMore = computed(() => this.selectedCount() < MAX_TEMPLATES);
  protected readonly hasNamesValid = computed(() => {
    const names = this.workflowNameByTemplateId();
    for (const tpl of this.selectedTemplatesOrdered()) {
      if (!(names[tpl.id] ?? '').trim()) return false;
    }
    return true;
  });
  protected readonly canInstall = computed(() => {
    if (this.installing()) return false;
    if (!this.vertical()) return false;
    if (this.selectedCount() === 0) return false;
    if (!this.hasNamesValid()) return false;
    if (this.effectiveProjectMode() === 'existing') return !!this.selectedProjectId();
    return this.newProjectName().trim().length > 0;
  });

  constructor() {
    // Garante uma tentativa de hidratação ao abrir o passo. Sem isso, se o
    // GET /api/companies não trouxer o campo `vertical` (DTO antigo / cliente
    // legacy), o estado fica preso em "Carregando...". Após hidratar, se
    // ainda for undefined, força null pra cair no fallback de seleção.
    void this.ensureVerticalResolved();

    effect(() => {
      const v = this.vertical();
      if (v) {
        void this.loadTemplates(v);
      } else {
        this.templates.set([]);
        this.loadingTemplates.set(false);
      }
    });

    effect(() => {
      const ordered = this.selectedTemplatesOrdered();
      this.workflowNameByTemplateId.update((current) => {
        const next: Record<string, string> = {};
        for (const tpl of ordered) {
          next[tpl.id] = current[tpl.id] ?? tpl.displayName;
        }
        return next;
      });
    });

    effect(() => {
      const list = this.projects();
      if (list.length === 0) {
        if (this.projectMode() !== 'new') this.projectMode.set('new');
        if (this.selectedProjectId() !== null) this.selectedProjectId.set(null);
        return;
      }
      if (
        list.length === 1 &&
        this.projectMode() === 'existing' &&
        !this.selectedProjectId()
      ) {
        this.selectedProjectId.set(list[0].id);
      }
    });
  }

  toggleTemplate(tpl: SystemWorkflowTemplate): void {
    const ids = this.selectedTemplateIds();
    if (ids.includes(tpl.id)) {
      this.selectedTemplateIds.set(ids.filter((id) => id !== tpl.id));
    } else {
      if (ids.length >= MAX_TEMPLATES) return;
      this.selectedTemplateIds.set([...ids, tpl.id]);
    }
    this.errorMessage.set(null);
    this.partialFailure.set(null);
  }

  isSelected(tpl: SystemWorkflowTemplate): boolean {
    return this.selectedTemplateIds().includes(tpl.id);
  }

  selectionOrder(tpl: SystemWorkflowTemplate): number {
    return this.selectedTemplateIds().indexOf(tpl.id) + 1;
  }

  getWorkflowName(templateId: string): string {
    return this.workflowNameByTemplateId()[templateId] ?? '';
  }

  setWorkflowName(templateId: string, name: string): void {
    this.workflowNameByTemplateId.update((cur) => ({ ...cur, [templateId]: name }));
  }

  setProjectMode(mode: 'existing' | 'new'): void {
    if (!this.hasProjects() && mode === 'existing') return;
    this.projectMode.set(mode);
    this.errorMessage.set(null);
  }

  toggleAppearancePopover(): void {
    this.appearancePopoverOpen.update((v) => !v);
  }

  closeAppearancePopover(): void {
    this.appearancePopoverOpen.set(false);
  }

  pickIcon(iconKey: string): void {
    this.newProjectIcon.set(iconKey);
  }

  pickColor(hex: string | null): void {
    this.newProjectColor.set(hex);
  }

  removeUploadedImage(): void {
    this.uploadedImageDataUrl.set(null);
    this.uploadedImageName.set('');
    this.uploadedProjectImageFile.set(null);
  }

  onProjectImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const imageDataUrl = typeof reader.result === 'string' ? reader.result : null;
      if (!imageDataUrl) return;
      this.uploadedImageDataUrl.set(imageDataUrl);
      this.uploadedImageName.set(file.name);
      this.uploadedProjectImageFile.set(file);
      this.newProjectIcon.set(null);
    };
    reader.readAsDataURL(file);
  }

  private async uploadProjectCoverIfNeeded(projectId: string): Promise<void> {
    const file = this.uploadedProjectImageFile();
    if (!file || !projectId) return;
    try {
      const formData = new FormData();
      formData.append('File', file);
      formData.append('file', file);
      await firstValueFrom(
        this.http.post(`/api/projects/${projectId}/cover`, formData)
      );
    } catch {
      // Falha silenciosa: não derruba o onboarding por causa de upload de imagem.
      // A capa fica como o ícone/cor escolhidos; o usuário pode tentar de novo nas
      // configurações do projeto depois.
    }
  }

  softenColor(hex: string | null | undefined): string {
    const safe = (hex ?? '').trim();
    if (!/^#([0-9a-f]{6})$/i.test(safe)) {
      return 'var(--accent-soft)';
    }
    return `${safe}22`;
  }

  async installAll(retry: boolean): Promise<void> {
    if (this.installing()) return;
    if (!retry && !this.canInstall()) return;

    let queue: SystemWorkflowTemplate[];
    let projectId: string | null;
    let installedSoFar: number;
    let totalCount: number;

    if (retry && this.partialFailure()) {
      const pf = this.partialFailure()!;
      queue = [pf.failedTemplate, ...pf.remaining];
      projectId = pf.capturedProjectId;
      installedSoFar = pf.okCount;
      totalCount = pf.totalCount;
    } else {
      queue = this.selectedTemplatesOrdered();
      projectId = this.effectiveProjectMode() === 'existing' ? this.selectedProjectId() : null;
      installedSoFar = 0;
      totalCount = queue.length;
    }

    let automationsDisabledTotal = 0;

    this.installing.set(true);
    this.errorMessage.set(null);
    this.partialFailure.set(null);
    this.installProgress.set({ index: installedSoFar, total: totalCount });

    try {
      for (let i = 0; i < queue.length; i++) {
        const tpl = queue[i];
        const orderInTotal = installedSoFar + i + 1;
        this.installProgress.set({ index: orderInTotal, total: totalCount });

        const workflowName = (this.workflowNameByTemplateId()[tpl.id] ?? tpl.displayName).trim();
        const payload: InstallSystemWorkflowTemplateRequest = projectId
          ? { projectId, workflowName }
          : {
              newProjectName: this.newProjectName().trim(),
              newProjectColor: this.newProjectColor() ?? null,
              newProjectIcon: this.newProjectIcon()
                ? projectIconKeyToApi(this.newProjectIcon()!)
                : null,
              workflowName,
            };

        try {
          const response = await firstValueFrom(this.api.install(tpl.id, payload));
          if (!projectId) {
            if (!response.createdProjectId) {
              throw new Error(this.i18n.t('onboarding.template.errornocreatedprojectid'));
            }
            projectId = response.createdProjectId;
            // Sobe a capa do projeto se o usuário escolheu uma imagem no popover de
            // aparência. Roda uma única vez (na criação) e ignora falhas silenciosas.
            await this.uploadProjectCoverIfNeeded(projectId);
          }
          automationsDisabledTotal += response.automationsDisabledCount;
        } catch (err) {
          const remaining = queue.slice(i + 1);
          const okSoFar = installedSoFar + i;
          const message =
            this.extractErrorMessage(err) ?? this.i18n.t('onboarding.template.errorgeneric');
          this.partialFailure.set({
            failedTemplate: tpl,
            remaining,
            capturedProjectId: projectId,
            errorMessage: message,
            okCount: okSoFar,
            totalCount,
          });
          if (okSoFar === 0) {
            this.errorMessage.set(message);
            this.partialFailure.set(null);
          }
          return;
        }
      }

      const successKey =
        totalCount === 1
          ? 'onboarding.template.toastsuccessone'
          : 'onboarding.template.toastsuccessmulti';
      this.toast.success(this.i18n.t(successKey, { count: totalCount }));
      if (automationsDisabledTotal > 0) {
        this.toast.show(
          this.i18n.t('onboarding.template.toastautomationsdisabled', {
            count: automationsDisabledTotal,
          }),
          'info',
          6000
        );
      }
      await this.store.advance(2);
      void this.checklistStore.refresh();
      // Invalida caches da sidebar e da lista de projetos pra refletir o(s)
      // projeto(s)/processo(s) recém-criados sem precisar de F5.
      void this.sidebarStore.loadSidebar({ force: true });
      this.projectsFacade.retry();
    } finally {
      this.installing.set(false);
      this.installProgress.set({ index: 0, total: 0 });
    }
  }

  async retryRemaining(): Promise<void> {
    await this.installAll(true);
  }

  async continueAnyway(): Promise<void> {
    if (this.installing()) return;
    this.partialFailure.set(null);
    await this.store.advance(2);
    void this.checklistStore.refresh();
  }

  async skipStep(): Promise<void> {
    if (this.installing()) return;
    await this.store.advance(2);
  }

  async snoozeNow(): Promise<void> {
    if (this.installing()) return;
    await this.store.snooze();
  }

  async confirmVerticalChoice(): Promise<void> {
    const chosen = this.verticalChooserValue();
    if (!chosen || this.verticalChooserSaving()) return;
    const id = this.companyContext.activeCompanyId();
    if (!id) return;

    this.verticalChooserSaving.set(true);
    this.verticalChooserError.set(null);
    try {
      await this.verticalApi.updateVertical(chosen, id);
      // Atualiza o store local — o effect de `vertical()` recarrega templates.
      this.companyStore.applyCompanyVertical(id, chosen);
    } catch (err) {
      const message =
        this.extractErrorMessage(err) ?? this.i18n.t('onboarding.template.verticalsaveerror');
      this.verticalChooserError.set(message);
    } finally {
      this.verticalChooserSaving.set(false);
    }
  }

  private async ensureVerticalResolved(): Promise<void> {
    if (this.verticalRaw() !== undefined) return;
    await this.companyStore.hydrateActiveCompanyVertical();
    if (this.verticalRaw() !== undefined) return;
    // Backend não devolveu o campo: trata como null (vazio explícito) pra
    // sair do loading e mostrar o fallback de seleção.
    const id = this.companyContext.activeCompanyId();
    if (id) this.companyStore.applyCompanyVertical(id, null);
  }

  private async loadTemplates(verticalKey: string): Promise<void> {
    this.loadingTemplates.set(true);
    try {
      const items = await firstValueFrom(this.api.list({ vertical: verticalKey }));
      this.templates.set(items);
    } catch {
      this.templates.set([]);
    } finally {
      this.loadingTemplates.set(false);
    }
  }

  private extractErrorMessage(err: unknown): string | null {
    if (typeof err === 'object' && err && 'message' in err) {
      const m = (err as { message: unknown }).message;
      if (typeof m === 'string' && m.trim()) return m;
    }
    return null;
  }
}
